import { useState } from "react";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ReportStatusBadge } from "./ReportStatusBadge";
import { useReport, useResolveReport } from "@/hooks/useReports";

const REPLY_MIN = 10;
const REPLY_MAX = 2000;

function formatDateTime(dateStr: string) {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateStr));
}

// Body component is keyed by reportId so opening a different report resets
// local reply state without needing useEffect to reset it.
function ReportDetailBody({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  const { data: report, isLoading, error } = useReport(reportId);
  const resolve = useResolveReport();
  const [reply, setReply] = useState("");

  const trimmed = reply.trim();
  const replyValid = trimmed.length >= REPLY_MIN && trimmed.length <= REPLY_MAX;

  const handleSubmit = async () => {
    if (!report || !replyValid) return;
    try {
      await resolve.mutateAsync({ id: report.id, adminReply: trimmed });
      toast.success("Zgłoszenie zostało rozwiązane i odpowiedź wysłana.");
      onClose();
    } catch (err) {
      toast.error("Nie udało się wysłać odpowiedzi.", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2 py-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-6 rounded bg-slate-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-destructive py-4 text-sm">
        Nie udało się załadować zgłoszenia: {error.message}
      </p>
    );
  }

  if (!report) return null;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div>
          <span className="text-muted-foreground">Status</span>
          <div className="mt-1">
            <ReportStatusBadge status={report.status} />
          </div>
        </div>
        <div>
          <span className="text-muted-foreground">Utworzono</span>
          <div className="mt-1">{formatDateTime(report.createdAt)}</div>
        </div>
        <div>
          <span className="text-muted-foreground">Zgłaszający</span>
          <div className="mt-1 truncate">{report.reporter.email}</div>
        </div>
        <div>
          <span className="text-muted-foreground">Wydarzenie</span>
          <div className="mt-1">
            {report.event ? (
              <span className="inline-flex items-center gap-1">
                <span className="truncate">{report.event.title}</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>
        </div>
      </div>

      <div>
        <span className="text-muted-foreground text-sm">Opis problemu</span>
        <div className="mt-1 rounded-md bg-muted/40 p-3 text-sm whitespace-pre-wrap">
          {report.description}
        </div>
      </div>

      {report.status === "resolved" ? (
        <div>
          <span className="text-muted-foreground text-sm">
            Odpowiedź administratora
            {report.repliedAt ? ` · ${formatDateTime(report.repliedAt)}` : ""}
            {report.resolvedBy ? ` · ${report.resolvedBy.email}` : ""}
          </span>
          <div className="mt-1 rounded-md border bg-background p-3 text-sm whitespace-pre-wrap">
            {report.adminReply ?? "—"}
          </div>
        </div>
      ) : (
        <div>
          <label className="text-sm font-medium" htmlFor="report-reply">
            Odpowiedź dla użytkownika
          </label>
          <Textarea
            id="report-reply"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Napisz krótką odpowiedź — zostanie wysłana jako powiadomienie."
            rows={5}
            className="mt-1"
            maxLength={REPLY_MAX}
          />
          <div className="mt-1 flex justify-end text-xs text-muted-foreground">
            {trimmed.length} / {REPLY_MAX}
          </div>
        </div>
      )}

      <DialogFooter>
        {report.status === "open" ? (
          <>
            <Button variant="outline" onClick={onClose} disabled={resolve.isPending}>
              Anuluj
            </Button>
            <Button onClick={handleSubmit} disabled={!replyValid || resolve.isPending}>
              {resolve.isPending ? "Wysyłanie…" : "Wyślij odpowiedź i oznacz jako rozwiązane"}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Zamknij</Button>
        )}
      </DialogFooter>
    </div>
  );
}

interface Props {
  reportId: string | null;
  onClose: () => void;
}

export function ReportDetailDialog({ reportId, onClose }: Props) {
  const open = reportId !== null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Zgłoszenie błędu</DialogTitle>
        </DialogHeader>
        {reportId !== null && (
          <ReportDetailBody key={reportId} reportId={reportId} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}
